/**
 * Redux Adapter
 * 
 * Implements the library-agnostic store interfaces using Redux Toolkit.
 * This is the only place where Redux-specific types should be referenced.
 */

import {
  configureStore,
  combineReducers,
  type Store,
  type UnknownAction,
  type Middleware as ReduxMiddleware,
  type Action as ReduxAction,
} from '@reduxjs/toolkit';
import {
  persistReducer,
  persistStore,
  type Persistor,
  type PersistConfig as ReduxPersistConfig,
} from 'redux-persist';
import type { Storage as ReduxPersistStorage } from 'redux-persist';
import type {
  IStore,
  IStoreConfig,
  IStoreWithPersistence,
  IPersistConfig,
  IPersistStorage,
  IPersistor,
  IReducer,
  IMiddleware,
  IAction,
  Thunk,
} from '../types';
import type { ISlice } from '../types';

/**
 * Redux Persist Storage Adapter
 * Adapts our IPersistStorage interface to Redux Persist's Storage interface
 */
class ReduxPersistStorageAdapter implements ReduxPersistStorage {
  constructor(private storage: IPersistStorage) {}

  async getItem(key: string): Promise<string> {
    const value = await this.storage.getItem(key);
    return value ?? '';
  }

  async setItem(key: string, value: string): Promise<void> {
    await this.storage.setItem(key, value);
  }

  async removeItem(key: string): Promise<void> {
    await this.storage.removeItem(key);
  }
}

/**
 * Redux Persistor Adapter
 * Adapts Redux Persist's Persistor to our IPersistor interface
 */
export class ReduxPersistorAdapter implements IPersistor {
  constructor(public persistor: Persistor) {}

  persist(): void {
    // Redux Persist automatically persists, no explicit method needed
  }

  async flush(): Promise<void> {
    await this.persistor.flush();
  }

  async purge(): Promise<void> {
    await this.persistor.purge();
  }

  pause(): void {
    this.persistor.pause();
  }

  resume(): void {
    // Redux Persist doesn't have a resume method, use pause/resume pattern
    // This is a no-op for compatibility
  }

  /**
   * Get the underlying Redux persistor (for backward compatibility)
   */
  getReduxPersistor(): Persistor {
    return this.persistor;
  }
}

/**
 * Redux Store Adapter
 * Wraps Redux store to implement IStore interface
 */
export class ReduxStoreAdapter<State = unknown> implements IStore<State> {
  private store: Store<State, UnknownAction>;

  constructor(store: Store<State, UnknownAction>) {
    this.store = store;
  }

  // Arrow function properties ensure `this` is always the adapter instance,
  // even when the method is stored as a reference (e.g. this._dispatch = store.dispatch).
  getState = (): State => {
    return this.store.getState();
  };

  dispatch = <T = unknown>(action: IAction<T> | Thunk<State, T>): IAction<T> | T | unknown => {
    if (typeof action === 'function') {
      return action(this.dispatch, this.getState) as T;
    }
    return this.store.dispatch(action as ReduxAction) as IAction<T>;
  };

  subscribe = (listener: () => void): () => void => {
    return this.store.subscribe(listener);
  };

  replaceReducer(reducer: IReducer<State>): void {
    this.store.replaceReducer(reducer as any);
  }

  /**
   * Get the underlying Redux store (for advanced use cases)
   */
  getReduxStore(): Store<State, UnknownAction> {
    return this.store;
  }
}

/**
 * Redux Middleware Adapter
 * Adapts Redux middleware to our IMiddleware interface
 */
export function createReduxMiddlewareAdapter<State = unknown>(
  middleware: IMiddleware<State>,
): ReduxMiddleware {
  return ((api: any) => {
    // Create a store-like object from the middleware API
    const adaptedStore: IStore<State> = {
      getState: () => api.getState(),
      dispatch: <T = unknown>(act: IAction<T> | Thunk<State, T>) => {
        if (typeof act === 'function') {
          // Thunk - call with dispatch and getState
          return act(api.dispatch as any, api.getState) as T;
        }
        return api.dispatch(act as ReduxAction) as IAction<T>;
      },
      subscribe: () => {
        // Middleware API doesn't have subscribe, return a no-op unsubscribe
        return () => {};
      },
    };
    
    return (next: any) => (action: any) => {
      const adaptedNext = (act: IAction) => next(act as ReduxAction) as IAction;
      const result = middleware(adaptedStore)(adaptedNext)(action as IAction);
      return result as ReduxAction;
    };
  }) as ReduxMiddleware;
}

/**
 * Create a Redux store with persistence support
 */
export function createReduxStore<State = unknown>(
  config: IStoreConfig<State> & { persistConfig?: IPersistConfig },
): IStoreWithPersistence<State> {
  const {
    reducer,
    middleware = [],
    devTools = true,
    preloadedState,
    persistConfig,
    reducerEnhancer,
  } = config;

  // Combine reducers if it's a map
  let rootReducer: IReducer<State>;
  let combinedReducersResult: ReturnType<typeof combineReducers> | null = null;
  
  if (typeof reducer === 'function') {
    rootReducer = reducer;
  } else {
    // Convert reducers map to combined reducer
    const reducersMap = reducer as Record<string, IReducer>;
    combinedReducersResult = combineReducers(
      Object.fromEntries(
        Object.entries(reducersMap).map(([key, reducerFn]) => [
          key,
          reducerFn as any,
        ]),
      ) as any,
    );
    rootReducer = combinedReducersResult as IReducer<State>;
  }

  // Apply persistence if configured
  let finalReducer = rootReducer;
  let persistor: Persistor | undefined;
  
  if (persistConfig) {
    const reduxPersistConfig: ReduxPersistConfig<State> = {
      key: persistConfig.key,
      storage: new ReduxPersistStorageAdapter(persistConfig.storage) as any,
      whitelist: persistConfig.whitelist,
      blacklist: persistConfig.blacklist,
    };
    
    finalReducer = persistReducer(reduxPersistConfig, rootReducer as any) as IReducer<State>;
  }

  // Apply reducer enhancer if provided (e.g., for logout state reset)
  if (reducerEnhancer) {
    finalReducer = reducerEnhancer(finalReducer);
  }

  // Convert middleware
  const reduxMiddleware = middleware.map((mw) =>
    createReduxMiddlewareAdapter(mw),
  );

  // Create Redux store
  const reduxStore = configureStore({
    reducer: finalReducer as any,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: persistConfig
          ? {
              ignoredActions: [
                'persist/PERSIST',
                'persist/REHYDRATE',
                'persist/FLUSH',
                'persist/PAUSE',
                'persist/PURGE',
                'persist/REGISTER',
              ],
            }
          : undefined,
      }).concat(reduxMiddleware as any),
    devTools,
    preloadedState: preloadedState as any,
  }) as Store<State, UnknownAction>;

  // Create persistor if persistence is enabled
  if (persistConfig) {
    persistor = persistStore(reduxStore);
  }

  const storeAdapter = new ReduxStoreAdapter(reduxStore);

  // Return store with persistence support
  // Add getReduxStore method for type inference
  const storeWithPersistence = Object.assign(storeAdapter, {
    persistor: persistor ? new ReduxPersistorAdapter(persistor) : undefined,
    getReduxStore: () => reduxStore,
  }) as IStoreWithPersistence<State> & { getReduxStore: () => Store<State, UnknownAction> };

  return storeWithPersistence;
}

/**
 * Convert Redux slice to our slice interface
 */
export function adaptReduxSlice<State = unknown>(
  slice: {
    name: string;
    reducer: IReducer<State>;
    actions: Record<string, (...args: any[]) => IAction>;
    initialState: State;
  },
): ISlice<State> {
  return {
    name: slice.name,
    reducer: slice.reducer,
    actions: slice.actions as any,
    initialState: slice.initialState,
  };
}
